import { useCallback, useEffect, useState } from 'react'
import { api, RequestFailed, type VariableRow } from '../api'
import { Icon } from './Icon'

/**
 * The variables surface: the words pages share, the way Library holds the
 * files they share.
 *
 * The number beside each row is the point of the screen. A writer about to
 * rename the product needs to know it lands on forty pages BEFORE saving,
 * not after — so the impact shows while a value is being edited, and the
 * page list is one click away, the same read-on-demand pattern as Library
 * usage. Every save writes variables.yaml onto the current draft through
 * the normal staged path: the change shows in review and publishes like
 * any page edit.
 */
const NAME_SHAPE = /^[a-z][a-z0-9_]*$/

/** A snippet is a filename, so it takes dashes where a variable takes underscores. */
const SNIPPET_NAME = /^[a-z][a-z0-9-]*$/

const IMPACT = (n: number) => `used on ${n} page${n === 1 ? '' : 's'}`

interface Missing {
  name: string
  pages: string[]
}

export function VariablesScreen({
  branch,
  readOnly,
  onStaged,
  onOpenPage,
  ensureDraft,
}: {
  /** The draft edits land on. Null means reading the live site. */
  branch: string | null
  readOnly: boolean
  /** Called after a successful save, so the review badge counts the change. */
  onStaged: () => void
  /** Open a content file in the editor — snippets are edited as files. */
  onOpenPage: (path: string) => void
  /**
   * The draft this edit needs, made on demand.
   *
   * This screen used to have a door on it: a read-only page with "Start a
   * draft" at the top, which had to be pressed before a single value could be
   * typed. Nothing about a variable needs that ceremony — the editor next
   * door makes its draft on the first keystroke and never mentions it — and
   * the door was the reason somebody arriving here found a screen that showed
   * them their variables and refused to let them change one.
   *
   * So the first save makes the draft, exactly as typing in a page does. It
   * resolves to the branch to write on, or null when a draft could not be
   * made (which is already said out loud by the caller).
   *
   * Left out for somebody who may not write: their draft would be refused by
   * the server, and this screen stays honestly read-only.
   */
  ensureDraft?: () => Promise<string | null>
}) {
  const [rows, setRows] = useState<VariableRow[] | null>(null)
  const [missing, setMissing] = useState<Missing[]>([])
  const [invalid, setInvalid] = useState<string | null>(null)
  const [revision, setRevision] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Value drafts by name, so typing never fights a reload. */
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [editing, setEditing] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newValue, setNewValue] = useState('')
  const [saved, setSaved] = useState<string | null>(null)

  /**
   * Read the file back — from the draft named, not from the one this render
   * was built with.
   *
   * The difference matters exactly once, and it is the save that MAKES the
   * draft: that save writes to a branch this render has never heard of, and a
   * reload that asked for `branch` would ask for the live site and be told,
   * correctly, that there are no variables on it. The screen then showed
   * "Nothing here yet" over a value that had just been saved.
   */
  const load = useCallback(
    async (on: string | null = branch) => {
      try {
        const result = await api.variables(on ?? undefined)
        setRows(result.variables)
        setMissing(result.missing)
        setInvalid(result.invalid)
        setRevision(result.revision)
        setDrafts(Object.fromEntries(result.variables.map((v) => [v.name, v.value])))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [branch],
  )

  useEffect(() => void load(), [load])

  /**
   * Every save sends the WHOLE map — the surface always has all of it — with
   * the revision it was read at, so two people editing variables at once get
   * a "changed underneath you" reload rather than a silent overwrite.
   */
  const saveMap = async (next: Record<string, string>) => {
    // The draft, or the one this save makes. Read before `setBusy`, because a
    // refusal to make one is not this screen's error to show.
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return
    setBusy(true)
    try {
      const out = await api.writeVariables(on, next, revision ?? undefined)
      setRevision(out.revision)
      await load(on)
      onStaged()
      setError(null)
      return true
    } catch (e) {
      if (e instanceof RequestFailed && e.code === 'stale_page') {
        setError('Somebody else changed the variables — reloaded, try again.')
        await load(on)
        return false
      }
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const currentMap = () => Object.fromEntries((rows ?? []).map((v) => [v.name, v.value]))

  const saveValue = async (row: VariableRow) => {
    setEditing((was) => (was === row.name ? null : was))
    const value = drafts[row.name] ?? row.value
    if (value === row.value) return
    if (await saveMap({ ...currentMap(), [row.name]: value })) {
      setSaved(row.name)
      window.setTimeout(() => setSaved((s) => (s === row.name ? null : s)), 1500)
    }
  }

  const addVariable = async (name: string, value: string) => {
    const trimmed = name.trim()
    if (!NAME_SHAPE.test(trimmed)) {
      setError(
        `"${trimmed}" is not a variable name — lowercase letters, digits and underscores, starting with a letter (like product_name).`,
      )
      return
    }
    if (await saveMap({ ...currentMap(), [trimmed]: value })) {
      setNewName('')
      setNewValue('')
    }
  }

  const remove = async (name: string) => {
    const next = currentMap()
    delete next[name]
    await saveMap(next)
  }

  if (!rows) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Variables</h1>
        </header>
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  const impactNote = (row: VariableRow) =>
    row.pages.length === 0
      ? 'Not used on any page yet — this change lands nowhere.'
      : `This change lands on ${row.pages.length} page${row.pages.length === 1 ? '' : 's'} when saved.`

  return (
    <div className="screen">
      <header className="screen__head">
        <h1>Variables</h1>
        <p className="screen__sub">
          Write <code>{'{{product_name}}'}</code> in a page and readers see the value below.
          Renaming the product is then one change here, not an edit on every page — each row says
          how many pages it lands on.
        </p>
      </header>

      {readOnly && (
        <p className="screen__warn">
          <Icon name="info" size={13} />
          {branch
            ? 'This draft is finished, so its variables are read-only.'
            : 'You have view access to this site, so these are read-only.'}
        </p>
      )}

      {invalid && (
        <p className="screen__warn screen__warn--problem">
          <Icon name="alert" size={13} /> variables.yaml is broken, so nothing substitutes until it
          is fixed: {invalid}
        </p>
      )}

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      {rows.length === 0 && !invalid ? (
        <p className="nothing">
          {readOnly ? (
            <>Nothing here yet, and nothing you can add while this is read-only.</>
          ) : (
            <>
              Nothing here yet. Add a value below, then write it into a page as{' '}
              <code>{'{{name}}'}</code>.
            </>
          )}
        </p>
      ) : (
        <ul className="vars__list">
          {rows.map((row) => {
            const dirty = (drafts[row.name] ?? row.value) !== row.value
            return (
              <li className="vars__row" key={row.name}>
                <div className="vars__line">
                  <code className="vars__name">{'{{' + row.name + '}}'}</code>
                  <input
                    className="library__alt vars__value"
                    value={drafts[row.name] ?? row.value}
                    disabled={readOnly || busy}
                    onFocus={() => setEditing(row.name)}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [row.name]: e.target.value }))}
                    onBlur={() => void saveValue(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                    }}
                  />
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => setOpen((o) => (o === row.name ? null : row.name))}
                    data-tip="The pages this variable is used on"
                  >
                    <Icon name="eye" size={12} />
                    {IMPACT(row.pages.length)}
                  </button>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => void remove(row.name)}
                      data-tip={
                        row.pages.length > 0
                          ? `Still referenced on ${row.pages.length} page${row.pages.length === 1 ? '' : 's'} — those will show {{${row.name}}} as text`
                          : 'Delete this variable'
                      }
                    >
                      {/* The label, not just the tooltip. Every other icon-only
                          button in the product names itself through the icon;
                          this one did not, so its accessible name came from
                          `title` alone — a name a touch user never sees and a
                          screen reader may or may not announce, on the one
                          control on this screen that destroys something. */}
                      <Icon name="trash" size={12} label={`Delete ${row.name}`} />
                    </button>
                  )}
                </div>

                {/* The impact preview: what saving this edit would touch,
                    shown while the value is being changed and BEFORE it is. */}
                {editing === row.name && dirty && (
                  <p className="vars__impact">
                    <Icon name="info" size={12} /> {impactNote(row)}
                  </p>
                )}
                {saved === row.name && (
                  <span className="library__saved">
                    <Icon name="check" size={11} /> Saved to this draft
                  </span>
                )}

                {open === row.name && (
                  <div className="library__usage">
                    {row.pages.length === 0 ? (
                      <span>Not used on any page.</span>
                    ) : (
                      <>
                        <span>Used on {row.pages.length === 1 ? 'this page' : 'these pages'}:</span>
                        <ul>
                          {row.pages.map((p) => (
                            <li key={p}>
                              <code>{p}</code>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {!readOnly && (
        <form
          className="vars__add"
          onSubmit={(e) => {
            e.preventDefault()
            void addVariable(newName, newValue)
          }}
        >
          <label className="library__field vars__add-name">
            <span>Name</span>
            <input
              className="library__alt"
              value={newName}
              placeholder="product_name"
              disabled={busy}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <label className="library__field vars__add-value">
            <span>Value</span>
            <input
              className="library__alt"
              value={newValue}
              placeholder="Adalo"
              disabled={busy}
              onChange={(e) => setNewValue(e.target.value)}
            />
          </label>
          <button className="btn btn--primary btn--sm" disabled={busy || newName.trim() === ''}>
            <Icon name="plus" size={12} />
            Add variable
          </button>
        </form>
      )}

      {missing.length > 0 && (
        <section className="vars__missing">
          <h2>Used on pages, not defined</h2>
          <p className="screen__sub">
            These names appear in page text but have no value, so readers see the braces. Define
            them here or fix the pages.
          </p>
          <ul className="vars__list">
            {missing.map((m) => (
              <li className="vars__row" key={m.name}>
                <div className="vars__line">
                  <code className="vars__name">{'{{' + m.name + '}}'}</code>
                  <span className="vars__meta">{IMPACT(m.pages.length)}</span>
                  {!readOnly && (
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => {
                        setNewName(m.name)
                        setNewValue('')
                      }}
                    >
                      <Icon name="plus" size={12} />
                      Define it
                    </button>
                  )}
                </div>
                <div className="library__usage">
                  <ul>
                    {m.pages.map((p) => (
                      <li key={p}>
                        <code>{p}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <SnippetsSection
        branch={branch}
        readOnly={readOnly}
        onStaged={onStaged}
        onOpenPage={onOpenPage}
        {...(ensureDraft ? { ensureDraft } : {})}
      />
    </div>
  )
}

/**
 * Shared BLOCKS, beside the shared words above: the support callout, the
 * prerequisite list — written once in `snippets/<name>.md`, shown wherever a
 * page references them. A snippet is a FILE, so editing one is ordinary
 * editing (open it, type, save, review) and its history is the draft
 * history; this section is the directory: what exists, where it is used, and
 * the one operation a file cannot offer — retiring without breaking pages.
 */
function SnippetsSection({
  branch,
  readOnly,
  onStaged,
  onOpenPage,
  ensureDraft,
}: {
  branch: string | null
  readOnly: boolean
  onStaged: () => void
  onOpenPage: (path: string) => void
  /** The draft a write needs, made on demand — see the same prop above. */
  ensureDraft?: () => Promise<string | null>
}) {
  const [rows, setRows] = useState<{ name: string; path: string; pages: string[] }[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retiring, setRetiring] = useState<{ name: string; pages: string[] } | null>(null)
  /**
   * The three things `window.prompt` and `window.alert` used to do here.
   *
   * A native prompt is the operating system interrupting the product: it
   * cannot be styled, cannot be reached by anything driving the page, empties
   * itself on a mis-click, and validates nothing — so the name typed into it
   * came back as free text and the refusal arrived afterwards, in a box
   * somewhere else on the screen. The replacement it was asking for is worse
   * still: it asked somebody to TYPE the name of another snippet, out of a
   * list printed into the prompt, and any typo was a request the server had to
   * refuse. It is a choice among things this screen already has in a variable.
   *
   * `naming` holds the new snippet's name while it is being typed, null while
   * the form is closed. `replaceWith` holds the snippet the references move
   * to, null while nothing is being replaced. `done` is what the alert said.
   */
  const [naming, setNaming] = useState<string | null>(null)
  const [replaceWith, setReplaceWith] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = useCallback(
    /** The draft named, for the reason the variables loader above states. */
    async (on: string | null = branch) => {
      try {
        const result = await api.snippets(on ?? undefined)
        setRows(result.snippets)
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [branch],
  )
  useEffect(() => void load(), [load])

  /** Every other snippet — what a retiring one's references can move to. */
  const others = (rows ?? []).map((r) => r.name).filter((n) => n !== retiring?.name)

  const create = async () => {
    const name = (naming ?? '').trim()
    if (!name) return
    if (!SNIPPET_NAME.test(name)) {
      setError('A snippet name is lowercase letters, digits and dashes, starting with a letter.')
      return
    }
    // The name is checked BEFORE a draft is made: a typo must not leave an
    // empty draft behind with nothing in it.
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return
    setBusy(true)
    try {
      await api.write(
        on,
        `snippets/${name}.md`,
        'Write the shared block here. Every page that references it shows this text.\n',
      )
      setNaming(null)
      setError(null)
      onStaged()
      onOpenPage(`snippets/${name}.md`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const retire = async (mode: 'detach' | 'replace') => {
    if (!retiring) return
    const replacement = mode === 'replace' ? (replaceWith ?? undefined) : undefined
    if (mode === 'replace' && !replacement) return
    const on = branch ?? (await ensureDraft?.()) ?? null
    if (!on) return
    setBusy(true)
    try {
      const result = await api.retireSnippet(retiring.name, on, mode, replacement)
      setRetiring(null)
      setReplaceWith(null)
      setError(null)
      onStaged()
      await load(on)
      setDone(
        result.pages.length === 0
          ? 'Nothing referenced it — the file is in the trash.'
          : `Done on ${result.pages.length} page${result.pages.length === 1 ? '' : 's'}, staged for review. The file is in the trash.`,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    /* Its own class, not `vars__missing`.
       The two sections looked alike so they shared a class, and the class
       MEANS "used on pages, not defined" — which the snippets list is not.
       The DOM said something false, a gate looking for the real one matched
       two elements and swallowed the strict-mode error as an empty string,
       and a check that should have failed loudly failed obscurely. */
    <section className="vars__snippets">
      <h2>Snippets</h2>
      <p className="home__quiet">
        Shared blocks. Reference one from a page with the Snippet item in the insert menu; edit the
        file and every page shows the change.
      </p>
      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}
      {/* What the alert said, said on the screen the act was performed on.
          An alert had to be dismissed before the list underneath could be
          read, which is the opposite of the order somebody wants: the
          sentence is about the rows that just changed. */}
      {done && (
        <p className="screen__note" role="status">
          <Icon name="check" size={13} /> {done}
        </p>
      )}
      {/* `if (rows === null) return null` used to stand where this does, so
          the whole Snippets section — its heading and the paragraph saying
          what a snippet IS — was absent from the screen and then appeared
          under the variables list, moving everything below it. The section is
          on the page from the first paint; only its list waits. */}
      {rows === null ? (
        <div className="screen__loading" aria-busy="true">
          <p className="sr-only">Loading…</p>
          <span className="skeleton" />
          <span className="skeleton" />
          <span className="skeleton" />
        </div>
      ) : rows.length === 0 ? (
        <p className="nothing">
          {readOnly
            ? 'Nothing shared yet, and nothing you can add while this is read-only.'
            : 'Nothing shared yet — the button below writes the first one.'}
        </p>
      ) : (
        <ul className="vars__list">
          {rows.map((row) => (
            <li className="vars__row" key={row.name}>
              <div className="vars__line">
                <code className="vars__name">{row.name}</code>
                <span className="vars__meta">
                  {row.pages.length === 0
                    ? 'not used on any page'
                    : `on ${row.pages.length} page${row.pages.length === 1 ? '' : 's'}`}
                </span>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => onOpenPage(row.path)}
                >
                  <Icon name="rename" size={12} />
                  Edit
                </button>
                {!readOnly && (
                  <button
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    onClick={() => {
                      setDone(null)
                      setReplaceWith(null)
                      setRetiring({ name: row.name, pages: row.pages })
                    }}
                  >
                    <Icon name="trash" size={12} />
                    Delete
                  </button>
                )}
              </div>
              {retiring?.name === row.name && (
                <div className="library__usage">
                  {retiring.pages.length === 0 ? (
                    <div className="review-actions">
                      <span>Nothing uses it — deleting breaks nothing.</span>
                      <button
                        className="btn btn--sm btn--outline"
                        disabled={busy}
                        onClick={() => void retire('detach')}
                      >
                        Delete it
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setRetiring(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : replaceWith !== null ? (
                    /* The choice the prompt used to ask somebody to TYPE.
                       Every snippet it could be is already in `rows`, so it is
                       a menu; typed, a single wrong character was a request
                       the server had to refuse. */
                    <div className="review-actions">
                      <span className="label">Point them at</span>
                      <select
                        className="select select--sm select--auto"
                        aria-label="The snippet those pages reference instead"
                        value={replaceWith}
                        disabled={busy}
                        onChange={(e) => setReplaceWith(e.target.value)}
                      >
                        {others.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn--sm btn--outline"
                        disabled={busy}
                        onClick={() => void retire('replace')}
                      >
                        Replace on {retiring.pages.length} page
                        {retiring.pages.length === 1 ? '' : 's'}
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={busy}
                        onClick={() => setReplaceWith(null)}
                      >
                        Back
                      </button>
                    </div>
                  ) : (
                    <div className="review-actions">
                      <span>
                        Used on {retiring.pages.length} page
                        {retiring.pages.length === 1 ? '' : 's'} — pick what those pages get:
                      </span>
                      <button
                        className="btn btn--sm btn--outline"
                        disabled={busy}
                        onClick={() => void retire('detach')}
                        data-tip="Each reference becomes the snippet's current text; the sharing ends"
                      >
                        Detach the text
                      </button>
                      <button
                        className="btn btn--sm btn--outline"
                        disabled={busy || others.length === 0}
                        onClick={() => setReplaceWith(others[0] ?? null)}
                        data-tip="Point every reference at another snippet"
                      >
                        Replace with another
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setRetiring(null)}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!readOnly &&
        (naming === null ? (
          <div className="home__actions">
            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                setError(null)
                setDone(null)
                setNaming('')
              }}
            >
              <Icon name="plus" size={13} /> New snippet
            </button>
          </div>
        ) : (
          /* The same form the variables above are added with, rather than a
             prompt drawn by the operating system: the rule the name has to
             obey is on the screen while it is being typed, instead of arriving
             as a refusal after the box has closed. */
          <form
            className="vars__add"
            onSubmit={(e) => {
              e.preventDefault()
              void create()
            }}
          >
            <label className="library__field vars__add-name">
              <span>Name the snippet</span>
              <input
                className="library__alt"
                autoFocus
                value={naming}
                placeholder="support-callout"
                disabled={busy}
                onChange={(e) => setNaming(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setNaming(null)
                }}
              />
            </label>
            <button className="btn" disabled={busy || naming.trim() === ''}>
              <Icon name="plus" size={13} /> Create
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setNaming(null)}>
              Cancel
            </button>
            <p className="field__note">
              Lowercase letters, digits and dashes, starting with a letter — it becomes the
              filename.
            </p>
          </form>
        ))}
    </section>
  )
}
